import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate, useParams } from 'react-router-dom';

// Email template editor with merge fields
function EmailEditor({ step, onChange, onRemove, stepIndex, mergeFields }) {
  const [showMergeFields, setShowMergeFields] = useState(false);

  const insertMergeField = (field) => {
    const textarea = document.getElementById(`body-${stepIndex}`);
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newBody = step.body.substring(0, start) + field + step.body.substring(end);
      onChange({ ...step, body: newBody });
    }
    setShowMergeFields(false);
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 relative">
      {/* Step Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">
            {stepIndex + 1}
          </div>
          <div>
            <h4 className="font-medium text-slate-900 dark:text-white">Step {stepIndex + 1}</h4>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {stepIndex === 0 ? 'Sent immediately after enrollment' : `Wait ${step.delay_days} day(s) ${step.delay_hours > 0 ? `${step.delay_hours}h` : ''} after previous step`}
            </p>
          </div>
        </div>
        
        {stepIndex > 0 && (
          <button
            onClick={onRemove}
            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
            title="Remove step"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>

      {/* Delay Settings (not for first step) */}
      {stepIndex > 0 && (
        <div className="flex items-center gap-4 mb-4 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
          <span className="text-sm text-slate-600 dark:text-slate-400">Wait</span>
          <input
            type="number"
            min="0"
            value={step.delay_days}
            onChange={(e) => onChange({ ...step, delay_days: parseInt(e.target.value) || 0 })}
            className="w-16 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded-lg text-center bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
          />
          <span className="text-sm text-slate-600 dark:text-slate-400">days</span>
          <input
            type="number"
            min="0"
            max="23"
            value={step.delay_hours}
            onChange={(e) => onChange({ ...step, delay_hours: parseInt(e.target.value) || 0 })}
            className="w-16 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded-lg text-center bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
          />
          <span className="text-sm text-slate-600 dark:text-slate-400">hours after previous email</span>
        </div>
      )}

      {/* Subject */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Subject Line</label>
        <input
          type="text"
          value={step.subject}
          onChange={(e) => onChange({ ...step, subject: e.target.value })}
          placeholder="e.g., Quick question, {first_name}"
          className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
        />
      </div>

      {/* Body */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Email Body</label>
          <div className="relative">
            <button
              onClick={() => setShowMergeFields(!showMergeFields)}
              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              Insert Merge Field
            </button>
            
            {showMergeFields && (
              <div className="absolute right-0 mt-1 w-56 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg z-10">
                <div className="p-2 border-b border-slate-100 dark:border-slate-700">
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Available Fields</p>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {mergeFields.map(field => (
                    <button
                      key={field.field}
                      onClick={() => insertMergeField(field.field)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 flex items-center justify-between"
                    >
                      <span className="font-mono text-blue-600">{field.field}</span>
                      <span className="text-xs text-slate-400">{field.example}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <textarea
          id={`body-${stepIndex}`}
          value={step.body}
          onChange={(e) => onChange({ ...step, body: e.target.value })}
          placeholder="Hi {first_name},&#10;&#10;I wanted to reach out about..."
          rows={8}
          className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
        />
        <p className="text-xs text-slate-400 mt-1">
          Use HTML for formatting. Merge fields like {'{first_name}'} will be replaced with actual values.
        </p>
      </div>
    </div>
  );
}

// Main Sequence Builder
export default function SequenceBuilder() {
  const { sequenceId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!sequenceId && sequenceId !== 'new';
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [mergeFields, setMergeFields] = useState([]);
  
  const [sequence, setSequence] = useState({
    name: '',
    description: '',
    trigger: 'manual',
    trigger_status: '',
    steps: [
      { id: crypto.randomUUID(), order: 0, subject: '', body: '', delay_days: 0, delay_hours: 0 }
    ],
    send_window_start: 9,
    send_window_end: 18,
    timezone: 'Asia/Kolkata',
    daily_limit: 50,
    stop_on_reply: true,
    stop_on_click: false
  });

  useEffect(() => {
    // Fetch merge fields
    axios.get(`${API}/sequences/merge-fields/list`).then(res => {
      setMergeFields(res.data.fields);
    });

    // Fetch sequence if editing
    if (isEditing) {
      axios.get(`${API}/sequences/${sequenceId}`).then(res => {
        setSequence(res.data);
        setLoading(false);
      }).catch(() => {
        navigate('/sequences');
      });
    }
  }, [sequenceId, isEditing, navigate]);

  const handleStepChange = (index, updatedStep) => {
    const newSteps = [...sequence.steps];
    newSteps[index] = updatedStep;
    setSequence({ ...sequence, steps: newSteps });
  };

  const handleAddStep = () => {
    setSequence({
      ...sequence,
      steps: [
        ...sequence.steps,
        { 
          id: crypto.randomUUID(), 
          order: sequence.steps.length, 
          subject: '', 
          body: '', 
          delay_days: 3, 
          delay_hours: 0 
        }
      ]
    });
  };

  const handleRemoveStep = (index) => {
    if (sequence.steps.length <= 1) return;
    const newSteps = sequence.steps.filter((_, i) => i !== index);
    setSequence({ ...sequence, steps: newSteps });
  };

  const handleSave = async (activate = false) => {
    if (!sequence.name.trim()) {
      alert('Please enter a sequence name');
      return;
    }

    if (sequence.steps.some(s => !s.subject.trim() || !s.body.trim())) {
      alert('Please fill in all email subjects and bodies');
      return;
    }

    setSaving(true);
    try {
      let savedSequence;
      
      if (isEditing) {
        await axios.put(`${API}/sequences/${sequenceId}`, sequence);
        savedSequence = { id: sequenceId };
      } else {
        const response = await axios.post(`${API}/sequences`, sequence);
        savedSequence = response.data;
      }

      if (activate) {
        await axios.post(`${API}/sequences/${savedSequence.id}/activate`);
      }

      navigate('/sequences');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save sequence');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="sequence-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {isEditing ? 'Edit Sequence' : 'Create New Sequence'}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Build your automated email campaign</p>
        </div>
        <button
          onClick={() => navigate('/sequences')}
          className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 dark:text-slate-300"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Basic Info */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Sequence Details</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Sequence Name *</label>
            <input
              type="text"
              value={sequence.name}
              onChange={(e) => setSequence({ ...sequence, name: e.target.value })}
              placeholder="e.g., New Lead Nurture, Post-Demo Follow-up"
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            />
          </div>
          
          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description</label>
            <input
              type="text"
              value={sequence.description}
              onChange={(e) => setSequence({ ...sequence, description: e.target.value })}
              placeholder="Brief description of this sequence"
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Trigger</label>
            <select
              value={sequence.trigger}
              onChange={(e) => setSequence({ ...sequence, trigger: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            >
              <option value="manual">Manual Enrollment</option>
              <option value="new_lead">When New Lead Added</option>
              <option value="status_change">When Status Changes</option>
            </select>
          </div>

          {sequence.trigger === 'status_change' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Trigger Status</label>
              <select
                value={sequence.trigger_status}
                onChange={(e) => setSequence({ ...sequence, trigger_status: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
              >
                <option value="">Select status...</option>
                <option value="contacted">Contacted</option>
                <option value="qualified">Qualified</option>
                <option value="meeting">Meeting Set</option>
                <option value="proposal">Proposal</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Settings */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Sending Settings</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Send Window Start</label>
            <select
              value={sequence.send_window_start}
              onChange={(e) => setSequence({ ...sequence, send_window_start: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            >
              {[...Array(24)].map((_, i) => (
                <option key={i} value={i}>{i}:00</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Send Window End</label>
            <select
              value={sequence.send_window_end}
              onChange={(e) => setSequence({ ...sequence, send_window_end: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            >
              {[...Array(24)].map((_, i) => (
                <option key={i} value={i}>{i}:00</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Daily Limit</label>
            <input
              type="number"
              min="1"
              max="500"
              value={sequence.daily_limit}
              onChange={(e) => setSequence({ ...sequence, daily_limit: parseInt(e.target.value) || 50 })}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Timezone</label>
            <select
              value={sequence.timezone}
              onChange={(e) => setSequence({ ...sequence, timezone: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            >
              <option value="Asia/Kolkata">India (IST)</option>
              <option value="UTC">UTC</option>
              <option value="America/New_York">US Eastern</option>
              <option value="Europe/London">UK</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-6 mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sequence.stop_on_reply}
              onChange={(e) => setSequence({ ...sequence, stop_on_reply: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">Stop when lead replies</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sequence.stop_on_click}
              onChange={(e) => setSequence({ ...sequence, stop_on_click: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">Stop when lead clicks link</span>
          </label>
        </div>
      </div>

      {/* Email Steps */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900 dark:text-white">Email Steps ({sequence.steps.length})</h3>
        </div>

        <div className="space-y-4">
          {sequence.steps.map((step, index) => (
            <EmailEditor
              key={step.id}
              step={step}
              stepIndex={index}
              mergeFields={mergeFields}
              onChange={(updated) => handleStepChange(index, updated)}
              onRemove={() => handleRemoveStep(index)}
            />
          ))}
        </div>

        <button
          onClick={handleAddStep}
          className="mt-4 w-full py-3 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl text-slate-500 dark:text-slate-400 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Another Step
        </button>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
        <button
          onClick={() => navigate('/sequences')}
          className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 dark:text-slate-100"
        >
          Cancel
        </button>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="px-6 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving}
            className="px-6 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save & Activate'}
          </button>
        </div>
      </div>
    </div>
  );
}
